import { HighlightType } from "@/grid/GridCellHighlightProvider";
import { EditableTextbox } from "@/overlay/components/CommonParts/EditableTextbox";
import { ManageActionPanel } from "@/overlay/components/LayerStackWindow/ManageActionPanel/ManageActionPanel";
import { SelectInput } from "@/overlay/components/SmartForm/HookInputs/SelectInput";
import { ModeSwitchableForm } from "@/overlay/components/SmartForm/SmartFormUtils/FormComponents/ModeSwitchableFormProps";
import { DefaultPlotSeries } from "@/reducer/client-models";
import { hasId } from "@/utility/typeGuard";
import { t } from "@lingui/core/macro";
import { COUNTRY_OPTIONS, COUNTRY_PLOT_PARAMETERS, replace10yMeanFromParameterName } from "../consts";
import { useCountryPlotForm } from "../controller";
import { PlotSeriesFieldsArray } from "./PlotSeriesFieldsArray";

/**
 * Responsible for rendering the whole layer form and send back the data on submit hadler.
 */
export function CountryPlotForm({ toolId }: { toolId: number }) {
  const { defaultFormData, submitMode, onSubmit, onRemove, onCancel, setHighlighted, formMethods, formValName } =
    useCountryPlotForm(toolId);

  const handleSubmitWithFormatting: typeof onSubmit = (formData) => {
    if (formData.internal?.defaultParameter) {
      // Future changes should enable a more flexible adjustment of plotSeries.
      // currently we have only two parameters
      const plotSeries = formData.plotData.plotSeries;

      // .internal?.defaultParameter is for the dropdown Select for the Parameter and
      // this internal.defaultParameter then changes the two plotSeries with that.
      // With the selected Parameter we swap the whole parameter set
      // i.e. t_2m_10y_mean:C set then plotSeries[0].parameter_unit ="t_2m_10y_mean:C"
      // and plotSeries[0].parameter_unit ="t_2m:C"
      if (plotSeries[0].parameter_unit !== formData.internal?.defaultParameter) {
        plotSeries[0] = {
          ...DefaultPlotSeries,
          ...plotSeries[0],
          parameter_unit: formData.internal?.defaultParameter,
        };
        plotSeries[1] = {
          ...DefaultPlotSeries,
          ...plotSeries[1],
          parameter_unit: replace10yMeanFromParameterName(formData.internal?.defaultParameter),
        };
      }

      formMethods.setValue("plotData.plotSeries", plotSeries);
      onSubmit({
        ...formData,
        plotData: { ...formData.plotData, plotSeries: plotSeries },
      });
    }
  };

  const formData = formMethods.watch();
  const plotSeries = formMethods.watch("plotData.plotSeries");

  return (
    <ModeSwitchableForm
      className="box-section"
      onSubmit={formMethods.handleSubmit(handleSubmitWithFormatting)}
      onCancel={onCancel}
      submitMode={submitMode}
      onMouseEnter={() => {
        if (hasId(defaultFormData)) {
          setHighlighted({
            type: HighlightType.COUNTRY_PLOT,
            id: defaultFormData.id,
          });
        }
      }}
      onMouseLeave={() => {
        setHighlighted(undefined);
      }}
    >
      <div className="title-row">
        <EditableTextbox
          className={"editable-map-title"}
          placeholder={"Untitled Country Plot"}
          value={formData.plotData.title}
          blurOnEnterKey
          onSave={(newTitle: string) => {
            onSubmit({
              ...formData,
              plotData: { ...formData.plotData, title: newTitle },
            });
          }}
        />
        <ManageActionPanel onRemove={onRemove} />
      </div>

      <SelectInput
        label={t`Country`}
        values={COUNTRY_OPTIONS}
        instruction={{
          currValue: defaultFormData.site_id,
          formValueName: formValName("plotData.site_id"),
          formMethods,
        }}
      />
      {/* TODO:  plotSeries how do show and add/edit/remove  */}
      <SelectInput
        label={t`Parameter`}
        values={COUNTRY_PLOT_PARAMETERS}
        instruction={{
          currValue:
            formData.plotData.plotSeries[0].parameter_unit ||
            formData.internal?.defaultParameter ||
            COUNTRY_PLOT_PARAMETERS[0].value,
          formValueName: formValName("internal.defaultParameter"),
          formMethods,
        }}
      />
      <PlotSeriesFieldsArray
        instruction={{
          formMethods,
          currValue: plotSeries,
          formValueName: formValName("plotData.plotSeries"),
        }}
        submitChange={(index, visible) => {
          const updatedPlotSeries = plotSeries.map((item, idx) => {
            if (idx === index) {
              return { ...item, visible };
            }
            return item;
          });
          onSubmit({
            ...formData,
            plotData: { ...formData.plotData, plotSeries: updatedPlotSeries },
          });
        }}
      />
    </ModeSwitchableForm>
  );
}
